import {
  Button,
  CreateButton,
  EditButton,
  Icons,
  List,
  Popconfirm,
  ShowButton,
  Space,
  Table,
  TableProps,
  notification,
} from '@pankod/refine-antd';
import { IResourceComponentsProps } from '@pankod/refine-core';
import CustomTableWithPagination from 'components/table/custom-table';
import { IRole, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useState } from 'react';
import { deleteRole, getRoles } from 'services/role';
import MetaData from 'utilities/metadata';

export const RoleList: React.FC<IResourceComponentsProps> = () => {
  const [roles, setRoles] = useState<IRole[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getRoles();
      setRoles(response);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleDelete = async (id: string) => {
    try {
      await deleteRole(id);
      fetchRoles();
      notification.success({
        message: 'Success',
        description: 'Role deleted',
      });
    } catch (err: any) {
      console.error(err.message);
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
  };

  const onTableChange: TableProps<IRole>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  return (
    <List headerProps={{ extra: <CreateButton type="primary">Create new role</CreateButton> }}>
      <MetaData />
      <CustomTableWithPagination
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={roles}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          pagination={false}
        >
          <Table.Column dataIndex="id" title="Role ID" />
          <Table.Column dataIndex="name" title="Name" />
          <Table.Column dataIndex="description" title="Description" />
          <Table.Column<IRole>
            title="Actions"
            dataIndex="actions"
            width={120}
            render={(_, record): React.ReactNode => {
              return (
                <Space>
                  <ShowButton size="small" recordItemId={record.id} hideText />
                  <EditButton size="small" recordItemId={record.id} hideText />
                  <Popconfirm
                    title="Are you sure?"
                    okText="Delete"
                    onConfirm={() => handleDelete(record.id)}
                  >
                    <Button size="small" danger icon={<Icons.DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};
